"use client"

import { useRef, useState, useEffect } from "react"
import styles from "./InfoSection.module.css"
import emailjs from '@emailjs/browser'
import { countryCodes } from "../../utils/countryCodes"

export default function InfoSection({
  sections = [],
  image = "/placeholder-syringe.jpg",
  formTitle = "GET FREE CONSULTATION",
  formSubtitle = "You need more?"
}) {
  // Form ref ve state'ler
  const formRef = useRef(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSending, setIsSending] = useState(false)
  
  // Ülke kodu dropdown state'leri
  const [selectedCountry, setSelectedCountry] = useState(() => (countryCodes.find(c => c.name === 'United States') || countryCodes[0]))
  const [phone, setPhone] = useState("")
  const [isCodeOpen, setIsCodeOpen] = useState(false)
  const codeWrapRef = useRef(null)

  // Modal'ı kapat
  const closeModal = () => {
    setIsSubmitted(false)
    document.body.style.overflow = 'unset'
  }

  // EmailJS ile form gönder
  const sendEmail = (e) => {
    e.preventDefault()
    setIsSending(true)
    const fd = new FormData(formRef.current)
    const params = {
      user_name: fd.get('user_name') || '',
      user_email: fd.get('user_email') || '',
      user_phone: fd.get('user_phone') || '',
      country_code: fd.get('country_code') || (selectedCountry?.dial_code || ''),
      user_phone_full: `${fd.get('country_code') || (selectedCountry?.dial_code || '')} ${fd.get('user_phone') || ''}`.trim(),
    }

    emailjs
      .send(
        'service_57xto7k',
        'template_ivxg7n9',
        params,
        { publicKey: 'OPDDEgsIGDGJBTjaz' }
      )
      .then(
        () => {
          setIsSubmitted(true)
          setIsSending(false)
          document.body.style.overflow = 'hidden'
          formRef.current && formRef.current.reset()
          setPhone('')
        },
        (error) => {
          console.error('Email sending failed:', error)
          setIsSending(false)
          alert('There was an error sending your message. Please try again in a short while.')
        }
      )
  }

  // Emoji flag oluştur
  const getFlagEmoji = (isoCode) => {
    try {
      if (!isoCode || isoCode.length !== 2) return "";
      const codePoints = isoCode
        .toUpperCase()
        .split("")
        .map(char => 127397 + char.charCodeAt(0))
      return String.fromCodePoint(...codePoints)
    } catch {
      return ""
    }
  }

  // Dış tıklamada ülke kodu dropdown'ını kapat
  useEffect(() => {
    if (!isCodeOpen) return
    const onClickOutside = (e) => {
      const el = codeWrapRef.current
      if (el && !el.contains(e.target)) {
        setIsCodeOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
    }
  }, [isCodeOpen])
  
  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.contentGrid}>
          {/* Sol panel - İçerik bölümü */}
          <div className={styles.leftPanel}>
            {sections.map((section, index) => (
              <div key={index}>
                <h2 className={styles.subheading}>{section.heading}</h2>
                {section.content.map((paragraph, pIndex) => {
                  if (paragraph === null && section.list) {
                    return (
                      <ul key={pIndex} className={styles.list}>
                        {section.list.map((item, itemIndex) => (
                          <li key={itemIndex} className={styles.listItem}>{item}</li>
                        ))}
                      </ul>
                    )
                  }
                  return paragraph ? (
                    <p key={pIndex} className={styles.text}>{paragraph}</p>
                  ) : null
                })}
              </div>
            ))}
          </div>

          {/* Sağ panel - Resim ve form */}
          <div className={styles.rightPanel}>
            <div className={styles.imageContainer}>
              <img 
                src={image} 
                alt="Autologous Micrograft Treatment"
                className={styles.image}
              />
            </div>
            
            <div className={styles.formContainer}>
              <form ref={formRef} className={styles.form} onSubmit={sendEmail}>
                <div className={styles.formHeader}>
                  <div className={styles.formTitle}>
                    <p className={styles.formSubtitle}>{formSubtitle}</p>
                    <h3 className={styles.formMainTitle}>{formTitle}</h3>
                  </div>
                  <div className={styles.formIcon}>💬</div>
                </div>
                
                <div className={styles.formField}>
                  <div className={styles.phoneWrapper}>
                    <div ref={codeWrapRef} className={styles.codeSelectWrap}>
                      <button
                        type="button"
                        className={styles.codeSelectTrigger}
                        onClick={() => setIsCodeOpen(!isCodeOpen)}
                      >
                        <span className={styles.codeLabel}>
                          {selectedCountry?.name || 'Select Country'}
                        </span>
                        <span className={styles.codeValue}>{selectedCountry?.dial_code || ''}</span>
                      </button>
                      {isCodeOpen && (
                        <div className={styles.codeList}>
                          {countryCodes.slice(0, 4).map((country) => (
                            <div key={`pinned-${country.code}`}>
                              <button
                                type="button"
                                className={styles.codeItem}
                                onClick={() => {
                                  setSelectedCountry(country)
                                  setIsCodeOpen(false)
                                }}
                              >
                                <span className={styles.codeLabel}>
                                  {country.name}
                                </span>
                                <span className={styles.codeValue}>{country.dial_code}</span>
                              </button>
                            </div>
                          ))}
                          <div className={styles.codeDivider} />
                          {countryCodes.slice(4).map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              className={styles.codeItem}
                              onClick={() => {
                                setSelectedCountry(country)
                                setIsCodeOpen(false)
                              }}
                            >
                              <span className={styles.codeLabel}>
                                {country.name}
                              </span>
                              <span className={styles.codeValue}>{country.dial_code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      className={styles.phoneInput}
                      type="tel"
                      name="user_phone"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                    <input type="hidden" name="country_code" value={selectedCountry?.dial_code || ''} />
                    <input type="hidden" name="user_phone_full" value={`${selectedCountry?.dial_code || ''} ${phone}`.trim()} />
                  </div>
                </div>
                
                <div className={styles.formField}>
                  <div className={styles.inputWrapper}>
                    <input 
                      className={styles.input}
                      type="text"
                      name="user_name"
                      placeholder="Full Name"
                      required
                    />
                  </div>
                </div>
                
                <div className={styles.formField}>
                  <div className={styles.inputWrapper}>
                    <input 
                      className={styles.input}
                      type="email"
                      name="user_email"
                      placeholder="Email Address"
                      required
                    />
                  </div>
                </div>
                
                <button type="submit" className={styles.submitBtn} disabled={isSending}>
                  {isSending ? 'Sending...' : formTitle}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Başarılı form gönderme modal'ı */}
      {isSubmitted && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeModal}>
              ×
            </button>
            <div className={styles.modalCard}>
              <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                <div style={{ marginBottom: 12 }}>
                  <svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="#13A672"/>
                    <path d="M20 32l8 8 14-14" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className={styles.modalTitle} style={{ marginTop: 0 }}>Thank you!</h3>
                <p style={{ margin: '6px 0 0', lineHeight: 1.5 }}>
                  Your request has been received successfully.<br />
                  Our medical consultants will contact you shortly to schedule your free consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
